import {
  autoUpdate,
  flip as flipMiddleware,
  offset as offsetMiddleware,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react'
import { useCallback, useEffect, useState } from 'react'

import type { UsePopoverProps } from './usePopover.types'

import { useCallbackRef } from '../utils'

type UsePopoverInternalProps = UsePopoverProps & {
  nodeId?: string
}

/**
 * Controls the logic of positioning and managing a floating popover.
 * Takes multiple configuration props that control closing, placement or behavior.
 * Exposes functions used for ref callbacks on consuming components, programmatic open/close methods and properties.
 */
export default function usePopover(props: UsePopoverInternalProps = {}) {
  const {
    closeOnBlur = true,
    closeOnEsc = true,
    defaultIsOpen = false,
    flip = true,
    isOpen: isOpenProp,
    matchWidth,
    offset,
    onClose: onCloseProp,
    onOpen: onOpenProp,
    nodeId,
    placement = 'bottom-start',
    trigger = 'click',
  } = props

  const isControlled = isOpenProp !== undefined
  const [isOpenState, setIsOpenState] = useState(defaultIsOpen)
  const isOpen = isControlled ? isOpenProp : isOpenState

  const onClose = useCallbackRef(onCloseProp)
  const onOpen = useCallbackRef(onOpenProp)

  const setIsOpen = useCallback(
    (open: boolean) => {
      if (!isControlled) {
        setIsOpenState(open)
      }
      if (open) {
        onOpen()
      } else {
        onClose()
      }
    },
    [isControlled, onOpen, onClose],
  )

  const close = useCallback(() => setIsOpen(false), [setIsOpen])
  const open = useCallback(() => setIsOpen(true), [setIsOpen])

  // Build middleware array
  const middleware = [
    offsetMiddleware({ mainAxis: offset?.[1] ?? 4, crossAxis: offset?.[0] ?? 0 }),
    flip && flipMiddleware(),
    shift({ padding: 5 }),
    matchWidth &&
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
  ].filter(Boolean)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    nodeId,
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  })

  // Opacity-only transition - no position movement
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
  })

  // Interaction hooks based on trigger type
  const clickEnabled = trigger === 'click' && !isControlled
  const hoverEnabled = trigger === 'mouseenter' && !isControlled
  const focusEnabled = (trigger === 'focus' || trigger === 'focusin') && !isControlled

  const click = useClick(context, { enabled: clickEnabled })
  const hover = useHover(context, {
    enabled: hoverEnabled,
    move: false,
    handleClose: hoverEnabled ? safePolygon() : null,
    delay: { open: 0, close: 200 }, // 200ms delay before closing
  })
  const focus = useFocus(context, { enabled: focusEnabled })
  const dismiss = useDismiss(context, {
    enabled: !isControlled,
    escapeKey: closeOnEsc,
    outsidePress: closeOnBlur,
  })
  const role = useRole(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, focus, dismiss, role])

  // Sync controlled state
  useEffect(() => {
    if (isControlled) {
      setIsOpenState(isOpenProp)
    }
  }, [isControlled, isOpenProp])

  return {
    close,
    context,
    floatingStyles,
    getFloatingProps,
    getReferenceProps,
    isMounted,
    isOpen,
    open,
    popper: refs.floating.current,
    reference: refs.reference.current as HTMLElement | null,
    refs,
    setPopper: refs.setFloating,
    setReference: refs.setReference,
    transitionStyles,
  }
}

export type UsePopoverType = ReturnType<typeof usePopover>
